/** @jsxImportSource solid-js */
import {
  createEffect,
  createSignal,
  For,
  JSXElement,
  Show,
  Signal,
} from 'solid-js';
import { t } from '@trimble/i18n';
import { CustomMapButtonDetails, MapTileModel } from './map-tile-model';

export interface MapTileModelSelectorProps<T extends string> {
  initialModelId: T | undefined;
  primaryTileModels: MapTileModel<T>[];
  secondaryTileModels?: MapTileModel<T>[];
  onTileModelChange?(tileModel: T): void;
  size?: number;
  additionalModels?: CustomMapButtonDetails[];
}

const DEFAULT_SIZE = 64;
const FONT_SIZE_RATIO = 7;
const LAYERS_IMAGE_SIZE_RATIO = 0.68;
const MAIN_IMAGE_PADDING_OFFSET = 8;

export const MapTileModelSelector = <T extends string>(
  props: MapTileModelSelectorProps<T>
): JSXElement => {
  const [currentTileModel, setCurrentTileModel]: Signal<
    MapTileModel<T> | undefined
  > = createSignal<MapTileModel<T> | undefined>(
    getTileModel(props.initialModelId)
  );

  createEffect(() => {
    if (props.primaryTileModels.length > 0 && props.initialModelId) {
      setCurrentTileModel(getTileModel(props.initialModelId));
    }
  });

  function otherTileModel(): MapTileModel<T> | undefined {
    return props.primaryTileModels.find(
      (l) => l.modelId !== currentTileModel()?.modelId
    );
  }

  function getTileModel(modelId: T | undefined): MapTileModel<T> | undefined {
    return (
      props.primaryTileModels.find((l) => l.modelId === modelId) ||
      props.secondaryTileModels?.find((l) => l.modelId === modelId)
    );
  }

  const getFontSize = (tileSize: number | undefined): string => {
    return `${((tileSize || DEFAULT_SIZE) / FONT_SIZE_RATIO).toString()}px`;
  };

  const getTileImgSize = (tileSize: number | undefined): string => {
    return `${(tileSize || DEFAULT_SIZE).toString()}px`;
  };

  // Currently only toggles between the first two tileModels.
  // When more tileModels are added it can be updated to toggle between the
  // previous and current tileModel.
  function toggleModelActive(): void {
    const otherModel = otherTileModel();
    if (otherModel) {
      setCurrentTileModel(otherModel);
      props.onTileModelChange?.(otherModel.modelId);
    }
  }

  const [mouseIsOver, setMouseIsOver] = createSignal(false);

  function getButtonActiveState(customButton: CustomMapButtonDetails): boolean {
    return customButton.isActive?.() ?? false;
  }

  function onCustomButtonClicked(customButton: CustomMapButtonDetails): void {
    customButton.onClick(!getButtonActiveState(customButton));
  }

  return (
    <div
      class={'flex flex-row'}
      onMouseEnter={() => {
        setMouseIsOver(true);
      }}
      onMouseLeave={() => {
        setMouseIsOver(false);
      }}
      data-testid="map-selector-main"
    >
      <div class={'bg-white bottom-0 rounded self-end'}>
        <div class={'flex flex-row p-1'}>
          <button onClick={toggleModelActive}>
            <div
              class={
                'relative flex flex-col justify-center items-center rounded'
              }
              style={{
                width: getTileImgSize(props.size! - MAIN_IMAGE_PADDING_OFFSET),
              }}
              data-testid="Selected-map-type"
            >
              <img
                alt={`Switch to ${
                  otherTileModel()?.label || 'other tile model'
                }`} // TODO: add i18n
                class="rounded-sm border bg-white border-white"
                src={otherTileModel()?.imgUrl}
                style={{
                  width: getTileImgSize(
                    props.size! - MAIN_IMAGE_PADDING_OFFSET
                  ),
                  ['aspect-ratio']: '1',
                }}
              />
              <div
                class={'w-full absolute bottom-0 text-white p-top-4'}
                style={{
                  ['font-size']: getFontSize(props.size),
                  background:
                    'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.5) 50%, #000000 100%)',
                }}
              >
                {otherTileModel()?.label}
              </div>
            </div>
          </button>
        </div>
      </div>
      <Show
        when={
          mouseIsOver() &&
          (props.secondaryTileModels?.length || props.additionalModels?.length)
        }
      >
        <div
          class="bg-white bottom-0 rounded ml-4"
          style={{
            left: getTileImgSize(props.size),
          }}
        >
          <div class="flex flex-row p-2 gap-2 items-center">
            <Show when={props.secondaryTileModels?.length}>
              <div
                data-testid="secondary-tile-models"
                id="secondary-tile-models"
                class="flex flex-row gap-1"
              >
                <For each={props.secondaryTileModels}>
                  {(tileModel) => {
                    const isSelected = (): boolean =>
                      currentTileModel()?.modelId === tileModel.modelId;
                    return (
                      <button
                        onClick={() => {
                          props.onTileModelChange?.(tileModel.modelId);
                          setCurrentTileModel(tileModel);
                        }}
                      >
                        <div
                          class={`flex flex-col justify-center items-center rounded hover:outline hover:outline-sky-500 ${
                            isSelected() ? 'outline outline-primary' : ''
                          }`}
                        >
                          <img
                            alt={`Switch to ${tileModel.label}`} // TODO: add i18n
                            class="rounded border border-white"
                            src={tileModel.imgUrl}
                            style={{
                              width: getTileImgSize(
                                props.size! * LAYERS_IMAGE_SIZE_RATIO
                              ),
                              ['aspect-ratio']: '1',
                            }}
                          />
                          <div
                            style={{
                              ['font-size']: getFontSize(props.size),
                            }}
                            data-testid={`map-type-${tileModel.label}`}
                          >
                            {tileModel.label}
                          </div>
                        </div>
                      </button>
                    );
                  }}
                </For>
              </div>
            </Show>
            <Show
              when={
                props.additionalModels?.length &&
                props.secondaryTileModels?.length
              }
            >
              <div
                data-testid="tile-models-separator"
                class="w-1 self-stretch bg-gray-1 rounded-full"
              />
            </Show>
            <Show when={props.additionalModels?.length}>
              <div
                data-testid="custom-toggle-buttons"
                class="flex flex-row gap-1"
              >
                <For each={props.additionalModels}>
                  {(customButton): JSXElement => {
                    const isActive = (): boolean =>
                      getButtonActiveState(customButton);
                    return (
                      <button
                        onClick={() => {
                          onCustomButtonClicked(customButton);
                        }}
                        data-testid={`custom-button-${customButton.label}`}
                      >
                        <div
                          class={`flex flex-col justify-center items-center rounded hover:outline hover:outline-sky-500 ${
                            isActive() ? 'outline outline-primary' : ''
                          }`}
                        >
                          <img
                            alt={`Switch to ${customButton.label}`}
                            class="rounded border border-white"
                            src={
                              isActive() && customButton.activeImgUrl
                                ? customButton.activeImgUrl
                                : customButton.imgUrl
                            }
                            style={{
                              width: getTileImgSize(
                                props.size! * LAYERS_IMAGE_SIZE_RATIO
                              ),
                              ['aspect-ratio']: '1',
                            }}
                          />
                          <div
                            style={{
                              ['font-size']: getFontSize(props.size),
                            }}
                          >
                            {t(customButton.label)}
                          </div>
                        </div>
                      </button>
                    );
                  }}
                </For>
              </div>
            </Show>
          </div>
        </div>
      </Show>
    </div>
  );
};
